<template><div></div></template>

<script>
import { mapActions, mapGetters } from 'vuex';
import offersHelper from '@/utils/helpers/offers';

export default {
  name: 'moveManager',
  props: {
    offer: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      demand: 'demandStore/demand',
      nbDaysBookingLimit: 'projectStore/nbDaysBookingLimit',
      offers: 'offerStore/offers',
      projectSelected: 'projectStore/projectSelected',
      servicesPrice: 'serviceStore/servicesPrice',
      servicesStore: 'serviceStore/services',
      prebookDate: 'comparatorStore/prebookDate',
    }),
    currentOffer() {
      return this.offer;
    },
    isCommissionaire() {
      return this.projectSelected && this.projectSelected.params.is_commissionaire;
    },
    isIndividual() {
      return this.projectSelected && this.projectSelected.params.is_individual;
    },
    isValidationDoneByPartner() {
      return this.projectSelected && this.projectSelected.params.partner_validates_quotes;
    },
    isMinarm() {
      return this.projectSelected && this.projectSelected.params.is_minarm;
    },
    hidePriceInfo() {
      return this.projectSelected && this.projectSelected.params.hide_price_info;
    },
    validOffer() {
      return offersHelper.getValidOffer(
        this.offers,
        this.isIndividual || !this.isValidationDoneByPartner,
      );
    },
    arrhesPercent() {
      return this.projectSelected && this.projectSelected.params.arrhes_percent;
    },
    isCBPaymentEnabled() {
      return this.projectSelected && !this.projectSelected.params.is_cb_payment_disabled;
    },
    calcArrhesAmount() {
      return this.currentOffer && (this.arrhesPercent / 100)
        * (this.$helpers.offers.getPrice(this.currentOffer, this.exclTax) + this.servicesPrice);
    },
    arrhesAmount() {
      return this.currentOffer && `${this.$n(this.calcArrhesAmount, 'currency')}`;
    },
    leftToDoInfos() {
      return {
        icon: false,
        subtitle: this.$t('resume.leftToDo.subtitle'),
        iconItem: ['fal', 'times-circle'],
      };
    },
    formuleInfos() {
      return {
        title: this.demand && this.demand.formule && this.demand.formule.title,
        subtitle: this.$t('resume.formule.subtitle'),
      };
    },
    isFinished() {
      return this.currentOffer && this.$helpers.offers.isFinished(this.offers);
    },
    isFinishedStatus() {
      return this.currentOffer && this.$helpers.offers.isFinishedStatus(this.currentOffer);
    },
    isSigned() {
      return this.currentOffer && this.$helpers.offers.isSigned(this.currentOffer);
    },
    isValidForPcd() {
      if (this.isIndividual || !this.isValidationDoneByPartner) {
        return this.currentOffer && this.$helpers.offers.isPrebooked(this.currentOffer);
      }
      return this.currentOffer && this.$helpers.offers.isValidForPcd(this.currentOffer);
    },
    isBookingCharge() {
      return this.isCommissionaire && ((this.isFinishedStatus
        && (this.isSigned || this.isFinished)) || this.isValidForPcd);
    },
    servicesInfos() {
      return {
        icon: ['fal', 'check-circle'],
        title: this.$t('resume.services.title'),
        subtitle: this.$t('resume.services.subtitle'),
      };
    },
    chosenOptionsInfos() {
      return {
        icon: ['fal', 'plus-circle'],
        title: this.$t('resume.options.title'),
        subtitle: this.$t('resume.chosenOptions.subtitle'),
        iconItem: ['fal', 'check-circle'],
      };
    },
    insuranceInfos() {
      return {
        icon: ['fal', 'shield-check'],
        title: this.$t('resume.insurance.title'),
        iconItem: ['fal', 'circle-check'],
        subtitle: this.$t(`resume.insurance.type.${this.currentOffer.assurance_type}`),
      };
    },
    optionsInfos() {
      return {
        icon: ['fal', 'plus-circle'],
        title: this.$t('resume.options.title'),
        subtitle: this.$t('resume.options.subtitle'),
        iconItem: ['fal', 'circle-xmark'],
        iconItemChosen: ['fal', 'circle-check'],
      };
    },
    minarmRightsLoader() {
      return () => import('../../components/details/MinarmRights.vue');
    },
    clientLoadDate() {
      return this.$helpers.demand.formatDates(this.demand.dep_date1, this.demand.dep_date2);
    },
    finalLoadDate() {
      return this.$helpers.demand.formatFinalLoadingDates(this.demand, this.currentOffer);
    },
    clientDeliveryDate() {
      return this.$helpers.demand.formatDates(this.demand.arr_date1, this.demand.arr_date2);
    },
    finalDeliveryDate() {
      return this.$helpers.demand.formatFinalDeliveryDates(this.demand, this.currentOffer);
    },
    formule() {
      return this.demand.formule
        && {
          ...this.formuleInfos,
          items: this.demand.formule.details,
        };
    },
    isInitialStatus() {
      return !!this.currentOffer && this.$helpers.offers.isInitialStatus(this.currentOffer);
    },
    isInitialState() {
      return this.offers
        && this.$helpers.offers.isInitialState(this.offers, this.projectSelected.params.prebooked);
    },
    leftToDo() {
      return this.demand.formule && this.demand.formule.left_to_do
        && {
          ...this.leftToDoInfos,
          items: this.demand.formule.left_to_do,
        };
    },
    services() {
      return this.demand.services && this.demand.services.services
        && {
          ...this.servicesInfos,
          items: this.demand.services.services,
        };
    },
    chosenOptions() {
      return this.demand.services && this.demand.services.chosen_options
        && {
          ...this.chosenOptionsInfos,
          items: this.demand.services.chosen_options,
        };
    },
    options() {
      return this.demand.services && this.demand.services.options
        && {
          ...this.optionsInfos,
          items: this.demand.services.options,
        };
    },
    calcPrice() {
      return this.currentOffer && this.$helpers.offers.getPrice(this.currentOffer, this.exclTax);
    },
    price() {
      // eslint-disable-next-line no-mixed-operators
      return this.currentOffer && this.$n(this.calcPrice * (this.arrhesPercent || 1) / 100, 'currency');
    },
    priceDiff() {
      return this.currentOffer && this.ceiling
        && Math.round((this.$helpers.offers.getPrice(this.currentOffer, this.exclTax)
        + this.servicesPrice - this.ceiling) * 100) / 100;
    },
    warningVisible() {
      return this.currentOffer && this.projectSelected.params.ceiling
        && this.ceiling > 0
        && this.priceDiff > 0;
    },
    exclTax() {
      return this.demand.show_price_ht;
    },
    priceType() {
      return this.exclTax ? 'price_ht' : 'price';
    },
    ceiling() {
      return this.currentOffer
        && this.$helpers.offers.getRepaymentCeiling(this.currentOffer, this.demand);
    },
    isRegisteredOnStripe() {
      return this.projectSelected && this.projectSelected.stripe_registered;
    },
    getTextParams() {
      return {
        price: this.$n(this.calcPrice, 'currency'),
        nbDaysBookingLimit: this.nbDaysBookingLimit,
        arrhesAmount: this.arrhesAmount,
        solde: this.$n(this.calcPrice - this.calcArrhesAmount, 'currency'),
        exclTax: this.exclTax ? 'HT' : 'TTC',
        moverName: this.currentOffer && this.currentOffer.mover && this.currentOffer.mover.name,
      };
    },
  },
  methods: {
    ...mapActions({
      preBookOffer: 'offerStore/preBookOffer',
      bookOffer: 'offerStore/bookOffer',
      chooseOffer: 'offerStore/chooseOffer',
    }),
    setOffer() {
      const offer = this.$helpers.offers.getValidOffer(this.offers, this.isIndividual);
      if (offer && offer.failed_payment) {
        this.offer = offer;
        this.offersLoading = false;
      }
    },
    getPrice(offer) {
      return this.$helpers.offers.getPrice(offer, this.exclTax);
    },
    getTextVersion(text, params) {
      return this.$helpers.pathText.getTextVersion(text, {
        ...params,
        isIndividual: this.isIndividual,
        arrhesPercent: this.arrhesPercent,
      });
    },
    getAddressArray(address) {
      return [{ address }];
    },
    getTextClient(client, content, contentDefault) {
      return client ? content : contentDefault;
    },
    async preBook(token) {
      this.$store.commit('configStore/SET_LOADING', true);
      const offer = {
        ...this.currentOffer,
        prebook_date: this.prebookDate,
        stripe_intent_id: token,
      };
      try {
        await this.preBookOffer(offer);
        this.$emit('update:offer', offer);
        return true;
      } catch (error) {
        return false;
      } finally {
        this.$store.commit('configStore/SET_LOADING', false);
      }
    },
    async book(token, paymentIntent, authenticateMethod) {
      this.$store.commit('configStore/SET_LOADING', true);
      const offer = {
        ...this.currentOffer,
      };
      if (token) {
        offer.stripe_intent_id = token;
      }
      if (paymentIntent) {
        offer.payment_intent = paymentIntent;
      }
      try {
        const data = await this.bookOffer(offer);
        this.$emit('update:offer', offer);
        this.$store.commit('configStore/SET_LOADING', false);
        return data;
      } catch (error) {
        // PAYMENT REFUSED - INDIVIDUAL CASE
        if (error && error.status === 402 && error.data) {
          const {
            error_code: errorCode,
            payment_intent: intent,
            decline_code: declineCode,
          } = error.data;
          if (authenticateMethod && intent && errorCode === 'authentication_required') {
            authenticateMethod(intent);
            return false;
          }

          const stripeCode = (errorCode === 'card_declined' && declineCode) || errorCode;
          this.$helpers.notificationWrapper('stripe', stripeCode);
          if (this.onStripeError) {
            this.onStripeError(stripeCode);
          }
        }
        if (error && error.status === 404) {
          this.$router.push({
            name: 'comparator',
          });
        }
        this.$store.commit('configStore/SET_LOADING', false);
        return false;
      }
    },
    async choose() {
      this.$store.commit('configStore/SET_LOADING', true);
      const offer = {
        ...this.currentOffer,
      };
      try {
        await this.chooseOffer(offer);
        this.$emit('update:offer', offer);
        return true;
      } catch (error) {
        return false;
      } finally {
        this.$store.commit('configStore/SET_LOADING', false);
      }
    },
  },
};
</script>
